import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useInventoryStore } from '../../store/inventoryStore';
import { useWarehouseStore } from '../../store/warehouseStore';
import { useStockActivityStore } from '../../store/stockActivityStore';
import FilterSelect from './FilterSelect';
import { fmtRequestDay } from '../../store/stockRequestStore';
import {
  ACTION_FILTERS,
  activityBadgeStyle,
  fmtLogDateTime,
} from '../../lib/stockActivity';

const LOG_TABLE_COLUMNS = [
  { key: 'date', label: 'Date', width: 148 },
  { key: 'action', label: 'Action', width: 140 },
  { key: 'item', label: 'Item', width: '18%' },
  { key: 'detail', label: 'Detail', width: '32%' },
  { key: 'user', label: 'User', width: '14%' },
  { key: 'reference', label: 'Reference', width: '16%' },
] as const;

const cellTruncate = 'px-3 py-1 text-xs overflow-hidden text-ellipsis whitespace-nowrap max-w-0';

const DateFilterField: React.FC<{
  value: string;
  onChange: (iso: string) => void;
  placeholder: string;
  ariaLabel: string;
  inputBg: string;
  border: string;
  text: string;
  muted: string;
}> = ({ value, onChange, placeholder, ariaLabel, inputBg, border, text, muted }) => {
  const pickerRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      el.showPicker?.();
    } catch {
      el.click();
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={openPicker}
        aria-label={ariaLabel}
        className="h-[26px] w-[7.25rem] text-xs px-2 rounded border text-left truncate focus:outline-none focus:ring-1 focus:ring-blue-500"
        style={{
          backgroundColor: inputBg,
          borderColor: border,
          color: value ? text : muted,
        }}
      >
        {value ? fmtRequestDay(value) : placeholder}
      </button>
      <input
        ref={pickerRef}
        type="date"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden
        style={{
          position: 'absolute',
          opacity: 0,
          pointerEvents: 'none',
          width: 0,
          height: 0,
          border: 0,
          padding: 0,
        }}
      />
    </div>
  );
};

const StockLogView: React.FC = () => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const fetchItems = useInventoryStore(s => s.fetchItems);
  const items = useInventoryStore(s => s.items);
  const warehouses = useWarehouseStore(s => s.warehouses);
  const { rows, isLoading, error, fetchActivity } = useStockActivityStore();

  const [actionFilter, setActionFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchItems();
    fetchActivity();
  }, [fetchItems, fetchActivity, warehouses.length]);

  const pageBg = isDark ? '#171a1f' : '#f3f4f6';
  const cardBg = isDark ? '#20242b' : '#ffffff';
  const border = isDark ? '#343a43' : '#e5e7eb';
  const headerBg = isDark ? '#20242b' : '#f0f1f3';
  const text = isDark ? '#e5e7eb' : '#111827';
  const muted = isDark ? '#9ca3af' : '#6b7280';
  const inputBg = isDark ? '#171a1f' : '#f9fafb';
  const rowHover = isDark ? '#262b35' : '#f9fafb';
  const filterActive = isDark ? '#60a5fa' : '#2563eb';

  const searchInputCls =
    'h-[26px] text-xs pl-7 pr-2 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 w-48';

  const itemNameById = useMemo(() => new Map(items.map(i => [i.id, i.name])), [items]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;

    return rows.filter(row => {
      if (actionFilter && row.actionKey !== actionFilter) return false;
      const t = row.createdAt.getTime();
      if (fromMs != null && t < fromMs) return false;
      if (toMs != null && t > toMs) return false;
      const itemName = row.itemId ? (itemNameById.get(row.itemId) ?? row.itemName) : row.itemName;
      if (q) {
        const hay = [
          row.actionLabel,
          itemName,
          row.detail,
          row.user,
          row.reference,
          row.unitLabel,
          row.serialMac,
          fmtLogDateTime(row.createdAt),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, actionFilter, dateFrom, dateTo, searchQuery, itemNameById]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: pageBg }}>
      <div className="px-4 pt-3 pb-0 flex-shrink-0">
        <div
          className="flex items-center gap-3 flex-nowrap px-3 py-1.5 rounded"
          style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
        >
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className="text-xs font-medium whitespace-nowrap"
              style={{ color: actionFilter ? filterActive : muted }}
            >
              Filter by action
            </span>
            <FilterSelect
              value={actionFilter}
              onChange={setActionFilter}
              placeholder="All actions"
              options={ACTION_FILTERS.map(a => ({ value: a.key, label: a.label }))}
              minWidth={150}
              inputBg={inputBg}
              border={border}
              text={text}
              muted={muted}
              activeColor={filterActive}
              panelBg={cardBg}
              rowHover={rowHover}
              searchable={false}
            />
          </div>

          <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: border }} />

          <div className="relative flex-shrink-0">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: muted }} />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search log…"
              className={searchInputCls}
              style={{
                backgroundColor: inputBg,
                borderColor: border,
                color: searchQuery ? text : muted,
              }}
            />
          </div>

          <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: border }} />

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className="text-xs font-medium whitespace-nowrap"
              style={{ color: dateFrom || dateTo ? filterActive : muted }}
            >
              Date range
            </span>
            <DateFilterField
              value={dateFrom}
              onChange={setDateFrom}
              placeholder="dd/mm/yyyy"
              ariaLabel="From date"
              inputBg={inputBg}
              border={border}
              text={text}
              muted={muted}
            />
            <span className="text-xs" style={{ color: muted }}>–</span>
            <DateFilterField
              value={dateTo}
              onChange={setDateTo}
              placeholder="dd/mm/yyyy"
              ariaLabel="To date"
              inputBg={inputBg}
              border={border}
              text={text}
              muted={muted}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-4 pt-3 pb-4 flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto rounded" style={{ border: `1px solid ${border}` }}>
          <table className="w-full table-fixed">
            <colgroup>
              {LOG_TABLE_COLUMNS.map(col => (
                <col
                  key={col.key}
                  style={{ width: typeof col.width === 'number' ? `${col.width}px` : col.width }}
                />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10" style={{ backgroundColor: headerBg }}>
              <tr style={{ borderBottom: `1px solid ${border}` }}>
                {LOG_TABLE_COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className="px-3 py-1.5 text-left text-[11px] uppercase tracking-wide font-medium whitespace-nowrap"
                    style={{ color: muted }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody style={{ backgroundColor: cardBg }}>
              {isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-xs" style={{ color: muted }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && error && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-xs text-red-400">
                    {error}
                  </td>
                </tr>
              )}
              {!isLoading && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-xs" style={{ color: muted }}>
                    {rows.length === 0 ? 'No activity recorded yet.' : 'No matching log entries.'}
                  </td>
                </tr>
              )}
              {filtered.map(row => {
                const badge = activityBadgeStyle(row.actionKey, isDark);
                const itemName = row.itemId ? (itemNameById.get(row.itemId) ?? row.itemName) : row.itemName;
                return (
                  <tr
                    key={row.id}
                    className="transition-colors"
                    style={{ borderBottom: `1px solid ${border}` }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = rowHover; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                  >
                    <td className={cellTruncate} style={{ color: muted }}>
                      {fmtLogDateTime(row.createdAt)}
                    </td>
                    <td className="px-3 py-1 overflow-hidden max-w-0">
                      <span
                        className="inline-flex max-w-full justify-center px-2 py-0.5 rounded-full text-[10px] font-medium truncate"
                        style={badge}
                        title={row.actionLabel}
                      >
                        {row.actionLabel}
                      </span>
                    </td>
                    <td className={cellTruncate} style={{ color: text }} title={itemName ? `${itemName}${row.unitLabel ? ` ${row.unitLabel}` : ''}` : undefined}>
                      {itemName || <span style={{ color: muted }}>—</span>}
                      {row.unitLabel && (
                        <span style={{ color: muted }}> {row.unitLabel}</span>
                      )}
                    </td>
                    <td className={cellTruncate} style={{ color: row.detail ? text : muted }} title={row.detail ?? undefined}>
                      {row.detail || '—'}
                    </td>
                    <td className={cellTruncate} style={{ color: muted }} title={row.user ?? undefined}>
                      {row.user || '—'}
                    </td>
                    <td className={cellTruncate} style={{ color: row.reference ? text : muted }} title={row.reference ?? undefined}>
                      {row.reference || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockLogView;
